import {
  Box,
  Group,
  Loader,
  Paper,
  Popover,
  Select,
  SegmentedControl,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconFileDownload, IconFilter } from '@tabler/icons-react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { gpSurfacesMapApi } from '../../lib/apis/gp-surfaces-map';
import { useGpSurfacesMapQueries } from '../../lib/queries/gp-surfaces-map';
import {
  DEFAULT_NGP_CAMPAIGN_YEARS,
  GpSurfacesMapClause,
  GpSurfacesMapContentMode,
  GpSurfacesMapDisplayMode,
  GpSurfacesMapMetricMode,
  GpSurfacesMapProjectionVolumeMode,
  GpSurfacesMapViewMode,
} from '../../lib/types/gp-surfaces-map';
import {
  applySamplerCoverageFillColors,
  colorMapFromOrganizations,
} from '../../lib/utils/sampler-coverage-map';
import { buildSamplerCoverageSvgFillPlan } from '../../lib/utils/sampler-coverage-svg-patterns';
import { formatCountFr, formatHaFr } from '../../lib/utils/gp-surfaces-format';
import {
  enrichGeoJsonWithChoroplethColors,
  loadFranceDepartmentsGeoJson,
  mergeDepartmentsWithGeoJson,
} from '../../lib/utils/gp-surfaces-geojson';
import {
  applyMapMetric,
  buildMapDepartments,
} from '../../lib/utils/gp-surfaces-map-departments';

import { GpSurfacesMapClauseControls } from './components/clause-controls';
import { GpSurfacesMapFilters } from './components/filters';
import { GpSurfacesMapboxView } from './components/gp-surfaces-mapbox-view';
import { GpSurfacesMapSamplerCoverageLegend } from './components/sampler-coverage-legend';
import { SamplerCoverageSvgPatternDefs } from './components/sampler-coverage-svg-pattern-defs';
import { GpSurfacesMapSamplerCoverageOrgTable } from './components/sampler-coverage-org-table';
import { GpSurfacesMapSamplerCoverageTable } from './components/sampler-coverage-table';
import { GpSurfacesMapSidebar } from './components/sidebar';

import { usePermissions } from '@auth/data-access';
import { useColFilter } from '@data-access';
import {
  buildGradientLegend,
  computeChoroplethStops,
  DepartmentFeatureProperties,
  FranceDepartmentsChoropleth,
  GradientLegend,
} from '@map';
import { FullMainHeight } from '@ui';

const resolveDefaultNgpYear = () => {
  const currentYear = new Date().getFullYear();
  if ((DEFAULT_NGP_CAMPAIGN_YEARS as readonly number[]).includes(currentYear)) {
    return currentYear;
  }
  return DEFAULT_NGP_CAMPAIGN_YEARS[DEFAULT_NGP_CAMPAIGN_YEARS.length - 1];
};

export const GpSurfacesMapPage: FC = () => {
  const { t } = useTranslation();
  const { isAdmin, isIndustrial } = usePermissions();
  const canExportSurfacesMap = isAdmin || isIndustrial;
  const canViewSamplerCoverage = isAdmin || isIndustrial;
  const gpSurfacesMapQueries = useGpSurfacesMapQueries();
  const [clause, setClause] = useState<GpSurfacesMapClause>('gp');
  const [campaignYear, setCampaignYear] = useState(resolveDefaultNgpYear);
  const organizationFilterName =
    clause === 'new' ? 'organizations_ngp' : 'organizations_gp';
  const [organizationFilter] = useColFilter<string[]>(
    'gpSurfacesMap',
    organizationFilterName,
  );
  const [viewMode, setViewMode] = useState<GpSurfacesMapViewMode>('actual');
  const [displayMode, setDisplayMode] =
    useState<GpSurfacesMapDisplayMode>('overview');
  const [projectionVolumeMode, setProjectionVolumeMode] =
    useState<GpSurfacesMapProjectionVolumeMode>('declared');
  const [metricMode, setMetricMode] =
    useState<GpSurfacesMapMetricMode>('hectares');
  const [contentMode, setContentMode] =
    useState<GpSurfacesMapContentMode>('surfaces');
  const [hoveredCode, setHoveredCode] = useState<string | null>(null);
  const [selectedDepartment, setSelectedDepartment] =
    useState<DepartmentFeatureProperties | null>(null);

  const organizationIds = useMemo(
    () =>
      (organizationFilter as string[] | null | undefined)?.map((id) =>
        Number(id),
      ) ?? [],
    [organizationFilter],
  );
  const selectedCampaignYear = clause === 'new' ? campaignYear : null;
  const isSamplerCoverageMode = contentMode === 'sampler_coverage';
  const withProjection = viewMode === 'with_projection';

  const mapDataQuery = useQuery({
    ...gpSurfacesMapQueries.map({
      clause,
      campaignYear: selectedCampaignYear,
      organizationIds,
      withProjection,
      uncertainVolumeDiscount:
        withProjection && projectionVolumeMode === 'conservative',
    }),
    placeholderData: keepPreviousData,
  });

  const exportMutation = useMutation({
    mutationFn: () =>
      gpSurfacesMapApi.exportExcel({
        clause,
        campaignYear: selectedCampaignYear,
        organizationIds,
        withProjection: true,
        uncertainVolumeDiscount:
          viewMode === 'with_projection' && projectionVolumeMode === 'conservative',
      }),
  });
  const availableCampaignYears =
    mapDataQuery.data?.available_campaign_years ?? DEFAULT_NGP_CAMPAIGN_YEARS;

  const geoJsonQuery = useQuery({
    queryKey: ['geo', 'fr-departments'],
    queryFn: loadFranceDepartmentsGeoJson,
    staleTime: Infinity,
  });

  const mapDepartments = useMemo(() => {
    const departments = buildMapDepartments(
      mapDataQuery.data?.departments ?? [],
      mapDataQuery.data?.projection,
      viewMode,
    );
    return applyMapMetric(departments, metricMode);
  }, [mapDataQuery.data, metricMode, viewMode]);

  const colorStops = useMemo(() => {
    const values = mapDepartments.map((department) => department.map_value);
    return computeChoroplethStops(values);
  }, [mapDepartments]);

  const samplerCoverage = mapDataQuery.data?.sampler_coverage;

  const samplerOrganizationColorMap = useMemo(
    () => colorMapFromOrganizations(samplerCoverage?.sampler_organizations ?? []),
    [samplerCoverage?.sampler_organizations],
  );

  const samplerCoverageSvgFillPlan = useMemo(() => {
    if (!isSamplerCoverageMode || !samplerCoverage) {
      return null;
    }
    return buildSamplerCoverageSvgFillPlan(
      samplerCoverage.departments,
      samplerOrganizationColorMap,
    );
  }, [
    isSamplerCoverageMode,
    samplerCoverage,
    samplerOrganizationColorMap,
  ]);

  const mergedGeoJson = useMemo(() => {
    if (!geoJsonQuery.data) {
      return null;
    }
    const merged = mergeDepartmentsWithGeoJson(geoJsonQuery.data, mapDepartments);
    if (isSamplerCoverageMode && samplerCoverage) {
      return applySamplerCoverageFillColors(
        merged,
        samplerCoverage.departments,
        samplerOrganizationColorMap,
      );
    }
    return enrichGeoJsonWithChoroplethColors(merged, colorStops);
  }, [
    colorStops,
    geoJsonQuery.data,
    isSamplerCoverageMode,
    mapDepartments,
    samplerCoverage,
    samplerOrganizationColorMap,
  ]);

  const metricModeOptions = useMemo(
    () => [
      {
        group: t('surfacesMap.metricMode.mapColoring'),
        items: [
          { value: 'hectares', label: t('surfacesMap.metricMode.hectares') },
        ],
      },
      {
        group: t('surfacesMap.tables.pointsSection'),
        items: [
          {
            value: 'carbon_points',
            label: t('surfacesMap.metricMode.carbonPoints'),
          },
          {
            value: 'granulo_points',
            label: t('surfacesMap.metricMode.granuloPoints'),
          },
          {
            value: 'total_sampling_points',
            label: t('surfacesMap.metricMode.totalSamplingPoints'),
          },
        ],
      },
      {
        group: t('surfacesMap.tables.analysesSection'),
        items: [
          {
            value: 'analysis_count',
            label: t('surfacesMap.metricMode.analysisCount'),
          },
          {
            value: 'granulo_analysis_count',
            label: t('surfacesMap.metricMode.granuloAnalysisCount'),
          },
          {
            value: 'total_analysis_count',
            label: t('surfacesMap.metricMode.totalAnalysisCount'),
          },
        ],
      },
    ],
    [t],
  );

  const gradientLegend = useMemo(() => {
    const formatValue = (value: number) => {
      if (metricMode === 'hectares') {
        return t('surfacesMap.legendValueHa', { value: formatHaFr(value) });
      }
      return t('surfacesMap.legendValueCount', {
        value: formatCountFr(value),
      });
    };
    return buildGradientLegend(colorStops, formatValue);
  }, [colorStops, metricMode, t]);

  const activeDepartment = useMemo(() => {
    if (selectedDepartment) {
      return selectedDepartment;
    }
    if (!hoveredCode || !mergedGeoJson) {
      return null;
    }
    const feature = mergedGeoJson.features.find(
      (item) => item.properties?.code === hoveredCode,
    );
    return feature?.properties ?? null;
  }, [hoveredCode, mergedGeoJson, selectedDepartment]);

  useEffect(() => {
    setHoveredCode(null);
    setSelectedDepartment(null);
  }, [clause, contentMode, selectedCampaignYear]);

  useEffect(() => {
    if (!canViewSamplerCoverage && contentMode === 'sampler_coverage') {
      setContentMode('surfaces');
    }
  }, [canViewSamplerCoverage, contentMode]);

  const samplerCoverageOrgTable = (
    <GpSurfacesMapSamplerCoverageOrgTable
      organizations={samplerCoverage?.sampler_organizations ?? []}
      unassignedTotals={
        samplerCoverage?.unassigned_totals ?? {
          actual_area: 0,
          projected_area: 0,
          combined_area: 0,
          projected_volume_t: 0,
          carbon_points: 0,
          analysis_count: 0,
          granulo_points: 0,
          granulo_analysis_count: 0,
          total_sampling_points: 0,
          total_analysis_count: 0,
        }
      }
      viewMode={viewMode}
    />
  );
  const samplerCoverageDeptTable =
    isSamplerCoverageMode && activeDepartment?.code ? (
      <GpSurfacesMapSamplerCoverageTable
        rows={samplerCoverage?.rows ?? []}
        selectedDepartmentCode={activeDepartment.code}
        viewMode={viewMode}
      />
    ) : null;

  const isInitialMapLoading =
    (mapDataQuery.isPending && !mapDataQuery.data) ||
    (geoJsonQuery.isPending && !geoJsonQuery.data);
  const isMapRefreshing =
    Boolean(mapDataQuery.data && mergedGeoJson && mapDataQuery.isFetching);
  const mapError =
    mapDataQuery.isError || geoJsonQuery.isError
      ? mapDataQuery.error ?? geoJsonQuery.error
      : null;

  return (
    <FullMainHeight.Root>
      <FullMainHeight.Header>
        <Paper>
          <Group justify="space-between" w="100%" wrap="nowrap">
            <Group gap="md" wrap="wrap">
              <Title order={2}>{t('app.nav.surfacesMap')}</Title>
              <GpSurfacesMapClauseControls
                availableCampaignYears={availableCampaignYears}
                campaignYear={campaignYear}
                clause={clause}
                onCampaignYearChange={setCampaignYear}
                onClauseChange={setClause}
              />
              {canViewSamplerCoverage ? (
                <SegmentedControl
                  data={[
                    {
                      label: t('surfacesMap.contentMode.surfaces'),
                      value: 'surfaces',
                    },
                    {
                      label: t('surfacesMap.contentMode.samplerCoverage'),
                      value: 'sampler_coverage',
                    },
                  ]}
                  onChange={(value) =>
                    setContentMode(value as GpSurfacesMapContentMode)
                  }
                  value={contentMode}
                />
              ) : null}
              <SegmentedControl
                data={[
                  {
                    label: t('surfacesMap.viewMode.actual'),
                    value: 'actual',
                  },
                  {
                    label: t('surfacesMap.viewMode.withProjection'),
                    value: 'with_projection',
                  },
                ]}
                onChange={(value) => setViewMode(value as GpSurfacesMapViewMode)}
                value={viewMode}
              />
              {viewMode === 'with_projection' ? (
                <SegmentedControl
                  data={[
                    {
                      label: t('surfacesMap.projectionVolumeMode.declared'),
                      value: 'declared',
                    },
                    {
                      label: t('surfacesMap.projectionVolumeMode.conservative'),
                      value: 'conservative',
                    },
                  ]}
                  onChange={(value) =>
                    setProjectionVolumeMode(
                      value as GpSurfacesMapProjectionVolumeMode,
                    )
                  }
                  value={projectionVolumeMode}
                />
              ) : null}
              <SegmentedControl
                data={[
                  {
                    label: t('surfacesMap.displayMode.overview'),
                    value: 'overview',
                  },
                  {
                    label: t('surfacesMap.displayMode.interactive'),
                    value: 'interactive',
                  },
                ]}
                onChange={(value) =>
                  setDisplayMode(value as GpSurfacesMapDisplayMode)
                }
                value={displayMode}
              />
            </Group>
            <Group gap="xs" wrap="nowrap">
              <Popover position="bottom-end" width={360}>
                <Popover.Target>
                  <Paper
                    component="button"
                    p="xs"
                    style={{ cursor: 'pointer', border: 'none' }}
                    withBorder
                  >
                    <Group gap="xs">
                      <IconFilter size={16} />
                      <Text size="sm">{t('table.filters')}</Text>
                    </Group>
                  </Paper>
                </Popover.Target>
                <Popover.Dropdown>
                  <GpSurfacesMapFilters
                    filterName={organizationFilterName}
                    organizations={mapDataQuery.data?.filters.organizations ?? []}
                  />
                </Popover.Dropdown>
              </Popover>
              {canExportSurfacesMap ? (
                <Paper
                  component="button"
                  disabled={!mapDataQuery.data || exportMutation.isPending}
                  onClick={() => exportMutation.mutate()}
                  p="xs"
                  style={{
                    border: 'none',
                    cursor:
                      !mapDataQuery.data || exportMutation.isPending
                        ? 'not-allowed'
                        : 'pointer',
                    opacity:
                      !mapDataQuery.data || exportMutation.isPending ? 0.6 : 1,
                  }}
                  withBorder
                >
                  <Group gap="xs">
                    {exportMutation.isPending ? (
                      <Loader size={16} />
                    ) : (
                      <IconFileDownload size={16} />
                    )}
                    <Text size="sm">{t('common.export')}</Text>
                  </Group>
                </Paper>
              ) : null}
            </Group>
          </Group>
        </Paper>
      </FullMainHeight.Header>
      <FullMainHeight.FlexContent>
        <FullMainHeight.Sidebar
          size={isSamplerCoverageMode ? 'xxl' : 'xl'}
        >
          <GpSurfacesMapSidebar
            activeDepartment={activeDepartment}
            contentMode={contentMode}
            projection={mapDataQuery.data?.projection}
            samplerCoverage={samplerCoverage}
            samplerCoverageDeptTable={samplerCoverageDeptTable}
            samplerCoverageOrgTable={samplerCoverageOrgTable}
            summary={mapDataQuery.data?.summary}
            viewMode={viewMode}
          />
        </FullMainHeight.Sidebar>
        <Box
          flex={1}
          h="100%"
          mih={0}
          style={{ alignSelf: 'stretch', minHeight: 0, minWidth: 0 }}
        >
          <Paper h="100%" p={0} radius="sm" withBorder>
            <Box h="100%" mih={320} pos="relative">
              {isInitialMapLoading ? (
                <Box
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    height: '100%',
                    justifyContent: 'center',
                    minHeight: 320,
                  }}
                >
                  <Group gap="sm">
                    <Loader size="sm" />
                    <Text size="sm">{t('surfacesMap.loading')}</Text>
                  </Group>
                </Box>
              ) : null}
              {mapError && !isInitialMapLoading ? (
                <Box
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    height: '100%',
                    justifyContent: 'center',
                    minHeight: 320,
                    padding: 16,
                  }}
                >
                  <Text c="red" size="sm" ta="center">
                    {t('surfacesMap.loadError')}
                  </Text>
                </Box>
              ) : null}
              {mergedGeoJson && !mapError && displayMode === 'overview' ? (
                <FranceDepartmentsChoropleth
                  colorStops={colorStops}
                  data={mergedGeoJson}
                  departmentFills={samplerCoverageSvgFillPlan?.fillsByCode}
                  fillColorMode={isSamplerCoverageMode ? 'feature' : 'gradient'}
                  hoveredCode={hoveredCode}
                  metricMode={metricMode}
                  onHover={setHoveredCode}
                  onSelect={(department) => setSelectedDepartment(department)}
                  selectedCode={selectedDepartment?.code ?? null}
                  svgDefs={
                    samplerCoverageSvgFillPlan ? (
                      <SamplerCoverageSvgPatternDefs
                        patterns={samplerCoverageSvgFillPlan.patterns}
                      />
                    ) : undefined
                  }
                />
              ) : null}
              {mergedGeoJson && !mapError && displayMode === 'interactive' ? (
                <GpSurfacesMapboxView
                  data={mergedGeoJson}
                  hoveredCode={hoveredCode}
                  onHover={setHoveredCode}
                  onSelect={(department) => setSelectedDepartment(department)}
                  selectedCode={selectedDepartment?.code ?? null}
                />
              ) : null}
              {isMapRefreshing ? (
                <Box
                  style={{
                    alignItems: 'center',
                    background: 'rgba(255, 255, 255, 0.45)',
                    display: 'flex',
                    height: '100%',
                    justifyContent: 'center',
                    left: 0,
                    position: 'absolute',
                    top: 0,
                    width: '100%',
                    zIndex: 2,
                  }}
                >
                  <Group gap="sm">
                    <Loader size="sm" />
                    <Text size="sm">{t('surfacesMap.loading')}</Text>
                  </Group>
                </Box>
              ) : null}

              <Paper
                left={16}
                p="sm"
                pos="absolute"
                shadow="sm"
                style={{ bottom: 16, maxWidth: 260, pointerEvents: 'auto', zIndex: 3 }}
                withBorder
              >
                <Stack gap="sm">
                  {isSamplerCoverageMode ? (
                    <GpSurfacesMapSamplerCoverageLegend
                      colorMap={samplerOrganizationColorMap}
                      samplerOrganizations={
                        samplerCoverage?.sampler_organizations ?? []
                      }
                    />
                  ) : (
                    <>
                      <Select
                        comboboxProps={{ withinPortal: false }}
                        data={metricModeOptions}
                        onChange={(value) =>
                          setMetricMode(
                            (value ?? 'hectares') as GpSurfacesMapMetricMode,
                          )
                        }
                        size="xs"
                        value={metricMode}
                      />
                      <GradientLegend entries={gradientLegend} />
                    </>
                  )}
                </Stack>
              </Paper>
            </Box>
          </Paper>
        </Box>
      </FullMainHeight.FlexContent>
    </FullMainHeight.Root>
  );
};
